<template>
  <div>
    <my-notification :icon="'cloud'">
      <template #title>
        {{ $t('compCarbonDocsEmissionsFactors.title-emfa') }}
      </template>
      <span v-html="$t('compCarbonDocsEmissionsFactors.text-emfa-file')" />.
    </my-notification>
    <br />
    <b-notification :closable="false">
      <b-field :label="$t('compCarbonDocsEmissionsFactors.field-1')">
        <b-select
          v-model="moduleToExport"
          :placeholder="$t('compCarbonDocsEmissionsFactors.field-1')"
          expanded
        >
          <option
            :value="module"
            v-for="(module, index) in modulesFactors"
            :key="index"
          >
            {{ $t(module) }}
          </option>
        </b-select>
        <p class="control">
          <b-button icon-left="download" type="is-primary" @click="csvExport()">
            {{ $t('compCarbonDocsEmissionsFactors.download') }}
          </b-button>
        </p>
      </b-field>
    </b-notification>

    <div
      style="
        position: absolute;
        left: 0;
        right: 0;
        margin-top: 30px;
        background: white;
      "
    >
      <b-tabs :animated="false" v-model="activeTab" multiline>
        <b-tab-item icon="car" value="vehicles" :label="$t('vehicles')">
          <emissions-factors-table
            v-if="activeTab === 'vehicles'"
            :factors="vehiclesFactors"
          />
        </b-tab-item>
        <b-tab-item icon="train" value="transports" :label="$t('transports')">
          <b-notification has-icon icon="info" :closable="false">
            <span v-html="$t('compCarbonDocsEmissionsFactors.transports-ef')" />
          </b-notification>

          <emissions-factors-table
            v-if="activeTab === 'transports'"
            :factors="transportsFactors"
          />
        </b-tab-item>
        <b-tab-item
          icon="thermometer-full"
          value="heatings"
          :label="$t('heatings')"
        >
          <emissions-factors-table
            v-if="activeTab === 'heatings'"
            :factors="heatingsFactors"
          />
        </b-tab-item>
        <b-tab-item icon="plug" value="electricity" :label="$t('electricity')">
          <emissions-factors-table
            v-if="activeTab === 'electricity'"
            :factors="electricityFactors"
          />
        </b-tab-item>
        <b-tab-item icon="tint" value="water" :label="$t('water')">
          <emissions-factors-table
            v-if="activeTab === 'water'"
            :factors="waterFactors"
          />
        </b-tab-item>
        <b-tab-item
          icon="textpattern"
          value="construction"
          :label="$t('construction')"
        >
          <emissions-factors-table
            v-if="activeTab === 'construction'"
            :factors="constructionFactors"
          />
        </b-tab-item>
        <b-tab-item
          icon="snowflake-o"
          value="refrigerants"
          :label="$t('refrigerants')"
        >
          <emissions-factors-table
            v-if="activeTab === 'refrigerants'"
            :factors="refrigerantsFactors"
          />
        </b-tab-item>
        <b-tab-item icon="eur" value="purchases" :label="$t('purchases')">
          <b-notification :closable="false">
            <span v-html="$t('compCarbonDocsEmissionsFactors.purchases-ef')" />
          </b-notification>
          <b-notification :closable="false">
            <span style="margin-top: 8px">
              <b-tag rounded type="is-info">How to cite</b-tag>&nbsp;
              <em
                >Purchases dominate the carbon footprint of research
                laboratories.</em
              >
              Marianne De Paepe, Laurent Jeanneau, Jérôme Mariette, Olivier
              Aumont, Andre Estevez-Torres. 2024. PLOS Sustain Transform 3(7):
              e0000116.
              <a
                target="_blank"
                class="link"
                href="https://doi.org/10.1371/journal.pstr.0000116"
              >
                10.1371/journal.pstr.0000116 </a
              >.
            </span>
          </b-notification>
          <emissions-factors-table
            v-if="activeTab === 'purchases'"
            :factors="purchasesFactors"
          />
        </b-tab-item>
        <b-tab-item icon="cutlery" value="foods" :label="$t('foods')">
          <div class="overflowcontent">
            <help-emissions-factors />
            <emissions-factors-table
              v-if="activeTab === 'foods'"
              :factors="foodsFactors"
            />
          </div>
        </b-tab-item>
        <b-tab-item
          icon="hubzilla"
          value="ractivities"
          :label="$t('ractivities')"
        >
          <emissions-factors-table
            v-if="activeTab === 'ractivities'"
            :factors="ractivitiesFactors"
          />
        </b-tab-item>
      </b-tabs>
    </div>
    <div v-if="activeTab === 'purchases'" style="margin-top: 950px" />
    <div v-else style="margin-top: 700px" />
  </div>
</template>

<script>
import { EmissionFactor } from '@/models/carbon/Factor.js'
import VEHICLES_FACTORS from '@/../data/factors/carbon/vehiclesFactors.json'
import TRANSPORTS_FACTORS from '@/../data/factors/carbon/transportsFactors.json'
import HEATING_FACTORS from '@/../data/factors/carbon/heatingFactors.json'
import ELECTRICITY_FACTORS from '@/../data/factors/carbon/electricityFactors.json'
import REFRIGERANTS_FACTORS from '@/../data/factors/carbon/refrigerantsFactors.json'
import PURCHASES_FACTORS from '@/../data/factors/carbon/purchasesFactors.json'
import WATER_FACTORS from '@/../data/factors/carbon/waterFactors.json'
import FOOD_FACTORS from '@/../data/factors/carbon/foodFactors.json'
import CONSTRUCTION_FACTORS from '@/../data/factors/carbon/constructionFactors.json'
import RACTIVITIES_FACTORS from '@/../data/factors/carbon/ractivitiesFactors.json'
import { createExportFile } from '@/utils/files'
import Modules from '@/models/Modules.js'

const MODULES_FACTORS = [
  Modules.VEHICLES,
  'transports',
  Modules.HEATINGS,
  Modules.ELECTRICITY,
  Modules.REFRIGERANTS,
  Modules.PURCHASES,
  Modules.RACTIVITIES
]
export default {
  name: 'EmissionsFactors',
  components: {
    'my-notification': () => import('@/components/MyNotification.vue'),
    'help-emissions-factors': () =>
      import('@/components/carbon/forms/foods/HelpEmissionsFactors.vue'),
    'emissions-factors-table': () =>
      import('@/components/carbon/docs/EmissionsFactorsTable.vue')
  },
  data () {
    return {
      activeTab: Modules.VEHICLES,
      modulesFactors: MODULES_FACTORS,
      moduleToExport: null
    }
  },
  computed: {
    vehiclesFactors: function () {
      let factors = []
      for (let subcategory of Object.keys(VEHICLES_FACTORS)) {
        for (let subsubcategory of Object.keys(VEHICLES_FACTORS[subcategory])) {
          factors.push(
            new EmissionFactor(
              VEHICLES_FACTORS[subcategory][subsubcategory].decomposition,
              Modules.VEHICLES,
              subcategory,
              subsubcategory,
              VEHICLES_FACTORS[subcategory][subsubcategory].description_FR,
              VEHICLES_FACTORS[subcategory][subsubcategory].description_EN,
              VEHICLES_FACTORS[subcategory][subsubcategory].unit,
              VEHICLES_FACTORS[subcategory][subsubcategory].group
            )
          )
        }
      }
      return factors
    },
    transportsFactors: function () {
      let factors = []
      for (let subcategory of Object.keys(TRANSPORTS_FACTORS)) {
        for (let subsubcategory of Object.keys(
          TRANSPORTS_FACTORS[subcategory]
        )) {
          factors.push(
            new EmissionFactor(
              TRANSPORTS_FACTORS[subcategory][subsubcategory].decomposition,
              'transports',
              subcategory,
              subsubcategory,
              TRANSPORTS_FACTORS[subcategory][subsubcategory].description_FR,
              TRANSPORTS_FACTORS[subcategory][subsubcategory].description_EN,
              TRANSPORTS_FACTORS[subcategory][subsubcategory].unit,
              TRANSPORTS_FACTORS[subcategory][subsubcategory].group
            )
          )
        }
      }
      return factors
    },
    heatingsFactors: function () {
      let factors = []
      for (let subcategory of Object.keys(HEATING_FACTORS)) {
        for (let subsubcategory of Object.keys(HEATING_FACTORS[subcategory])) {
          let rsubsubcat = subsubcategory
          if (subsubcategory.startsWith('network')) {
            rsubsubcat =
              HEATING_FACTORS[subcategory][subsubcategory].description_FR
          }
          factors.push(
            new EmissionFactor(
              HEATING_FACTORS[subcategory][subsubcategory].decomposition,
              Modules.HEATINGS,
              subcategory,
              rsubsubcat,
              HEATING_FACTORS[subcategory][subsubcategory].description_FR,
              HEATING_FACTORS[subcategory][subsubcategory].description_EN,
              HEATING_FACTORS[subcategory][subsubcategory].unit,
              HEATING_FACTORS[subcategory][subsubcategory].group
            )
          )
        }
      }
      return factors
    },
    electricityFactors: function () {
      let factors = []
      for (let subcategory of Object.keys(ELECTRICITY_FACTORS)) {
        factors.push(
          new EmissionFactor(
            ELECTRICITY_FACTORS[subcategory].decomposition,
            Modules.ELECTRICITY,
            subcategory,
            null,
            ELECTRICITY_FACTORS[subcategory].description_FR,
            ELECTRICITY_FACTORS[subcategory].description_EN,
            ELECTRICITY_FACTORS[subcategory].unit,
            ELECTRICITY_FACTORS[subcategory].group
          )
        )
      }
      return factors
    },
    refrigerantsFactors: function () {
      let factors = []
      for (let subcategory of Object.keys(REFRIGERANTS_FACTORS)) {
        factors.push(
          new EmissionFactor(
            REFRIGERANTS_FACTORS[subcategory].decomposition,
            Modules.REFRIGERANTS,
            REFRIGERANTS_FACTORS[subcategory].description_FR,
            null,
            REFRIGERANTS_FACTORS[subcategory].description_FR,
            REFRIGERANTS_FACTORS[subcategory].description_EN,
            REFRIGERANTS_FACTORS[subcategory].unit,
            REFRIGERANTS_FACTORS[subcategory].group
          )
        )
      }
      return factors
    },
    waterFactors: function () {
      return [
        new EmissionFactor(
          WATER_FACTORS.decomposition,
          Modules.WATER,
          WATER_FACTORS.description_FR,
          null,
          WATER_FACTORS.description_FR,
          WATER_FACTORS.description_EN,
          WATER_FACTORS.unit,
          WATER_FACTORS.group
        )
      ]
    },
    constructionFactors: function () {
      return [
        new EmissionFactor(
          CONSTRUCTION_FACTORS.decomposition,
          Modules.CONSTRUCTION,
          CONSTRUCTION_FACTORS.description_FR,
          null,
          CONSTRUCTION_FACTORS.description_FR,
          CONSTRUCTION_FACTORS.description_EN,
          CONSTRUCTION_FACTORS.unit,
          CONSTRUCTION_FACTORS.group
        )
      ]
    },
    purchasesFactors: function () {
      let factors = []
      for (let subcategory of Object.keys(PURCHASES_FACTORS)) {
        factors.push(
          new EmissionFactor(
            PURCHASES_FACTORS[subcategory].decomposition,
            Modules.PURCHASES,
            PURCHASES_FACTORS[subcategory].category,
            subcategory.toUpperCase(),
            PURCHASES_FACTORS[subcategory].description_FR,
            PURCHASES_FACTORS[subcategory].description_FR,
            PURCHASES_FACTORS[subcategory].unit,
            PURCHASES_FACTORS[subcategory].group
          )
        )
      }
      return factors
    },
    ractivitiesFactors: function () {
      let factors = []
      for (let subcategory of Object.keys(RACTIVITIES_FACTORS)) {
        let scat = RACTIVITIES_FACTORS[subcategory]
        for (let subsubcategory of Object.keys(scat)) {
          let sscat = scat[subsubcategory]
          for (let subsubsubcategory of Object.keys(sscat)) {
            let ssscat = sscat[subsubsubcategory]
            factors.push(
              new EmissionFactor(
                ssscat.decomposition,
                subcategory,
                subsubcategory,
                subsubsubcategory,
                ssscat.description_FR,
                ssscat.description_EN,
                ssscat.unit,
                ssscat.group
              )
            )
          }
        }
      }
      return factors
    },
    foodsFactors: function () {
      let factors = []
      for (let subcategory of Object.keys(FOOD_FACTORS)) {
        factors.push(
          new EmissionFactor(
            FOOD_FACTORS[subcategory].decomposition,
            Modules.FOODS,
            FOOD_FACTORS[subcategory].description_FR,
            null,
            FOOD_FACTORS[subcategory].description_FR,
            FOOD_FACTORS[subcategory].description_EN,
            FOOD_FACTORS[subcategory].unit,
            FOOD_FACTORS[subcategory].group
          )
        )
      }
      return factors
    }
  },
  methods: {
    csvExport () {
      let fname = this.moduleToExport + 'Factors'
      let self = this
      let csvContent = [
        [
          'category',
          'subcategory',
          'subsubcategory',
          'unit',
          'name',
          'year',
          'co2',
          'ch4',
          'n2o',
          'other',
          'total',
          'uncertainty',
          'ef.unit'
        ].join('\t'),
        ...this[fname].map(function (item) {
          let cfactor = item.getFactor()
          let toReturn =
            self.$t(item.category) + '\t' + self.$t(item.subcategory) + '\t'
          toReturn +=
            self.$t(item.subsubcategory) + '\t' + self.$t(item.unit) + '\t'
          if (self.$i18n.locale === 'fr') {
            toReturn += item.descriptionFR + '\t'
          } else {
            toReturn += item.descriptionEN + '\t'
          }
          toReturn += item.getYear() + '\t' + cfactor.total.co2 + '\t'
          toReturn += cfactor.total.ch4 + '\t' + cfactor.total.n2o + '\t'
          toReturn += cfactor.total.other + '\t' + cfactor.total.total + '\t'
          toReturn +=
            cfactor.total.uncertainty +
            '\t' +
            self.$t('kg.co2e') +
            '/' +
            self.$t(item.unit)
          return toReturn
        })
      ]
        .join('\n')
        .replace(/(^\[)|(\]$)/gm, '')
      createExportFile(csvContent, this.moduleToExport + '_factors')
    }
  }
}
</script>

<style></style>
